import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { BulkEquipeData } from '@/types/bulk';
import { toast } from 'sonner';
import { getPictogrammeEquipeById } from '@/constants/pictogrammesEquipe';

interface UseCreateEquipesReturn {
  createEquipes: (equipes: BulkEquipeData[], entrepriseId: string) => Promise<boolean>;
  isLoading: boolean;
  error: string | null;
}

export function useCreateEquipes(): UseCreateEquipesReturn {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const createEquipes = async (equipes: BulkEquipeData[], entrepriseId: string): Promise<boolean> => {
    setIsLoading(true);
    setError(null);

    try {
      // Préparer les données pour l'insertion (sans l'id temporaire)
      // Si spécialité vide, utiliser le label du pictogramme par défaut
      const equipesToInsert = equipes.map(({ id, ...equipe }) => {
        const specialiteFinale = equipe.specialite?.trim()
          || getPictogrammeEquipeById(equipe.pictogramme)?.label
          || '';
        return {
          ...equipe,
          specialite: specialiteFinale,
          entreprise_id: entrepriseId,
        };
      });

      const { data, error: insertError } = await supabase
        .from('equipes')
        .insert(equipesToInsert)
        .select();

      if (insertError) throw insertError;

      toast.success(`${equipes.length} équipe(s) créée(s) avec succès`);
      return true;
    } catch (err: any) {
      const message = err.message || 'Erreur lors de la création des équipes';
      setError(message);
      toast.error(message);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  return { createEquipes, isLoading, error };
}
